<script setup lang="ts">
import TagElement from '~/components/tag/element.vue'
import TagGridTemplate from '~/components/tag/gridTemplate.vue'

import { useSortTagsByTranslatedName } from '~/composables/useTagHelpers'

import { useTagStore } from '~/stores/tag'

import type Tag from '~/entities/tag'
import type TagType from '~/entities/tagType'
import type TagTypeKey from '~/types/tagTypeKey'

type AvailableSpecialTagNames = 'none'

type Props = {
  maxAmount?: number
  disallowed?: number[]
  includeUnderscoreTags?: boolean
  includeSpecialTag?: AvailableSpecialTagNames | undefined
  typeName: TagTypeKey
  modelValue: number[]
  readonly?: boolean
  ignoreXContentPad?: boolean
}

type Emits = {
  'update:modelValue': [payload: number[]]
}

const props = withDefaults(defineProps<Props>(), {
  maxAmount: 0,
  disallowed: () => [],
  includeUnderscore: false,
  includeSpecialTag: undefined,
  readonly: false,
  ignoreXContentPad: false,
})
const emit = defineEmits<Emits>()

const sortTagsByTranslatedName = useSortTagsByTranslatedName()

const { GET_TAG_TYPE_FROM_NAME, GET_TAGS_FROM_IDS } = useTagStore()

const TAG_TYPE = computed<TagType | undefined>(() =>
  GET_TAG_TYPE_FROM_NAME(props.typeName),
)

const AVAILABLE_TAGS = computed<Tag[]>(() => {
  return (<Tag[]>GET_TAGS_FROM_IDS(TAG_TYPE.value?.tag_ids ?? []))
    .filter((tag: Tag): boolean => {
      return (
        ((props.includeSpecialTag?.length &&
          tag.name.includes(
            props.includeSpecialTag as AvailableSpecialTagNames,
          )) ||
          props.includeUnderscoreTags ||
          !tag.name.includes('_')) &&
        !props.disallowed.includes(tag.id)
      )
    })
    .sort((a, b) => sortTagsByTranslatedName(props.typeName, a, b))
})

const SPECIAL_TAG = computed<Tag | undefined>(() => {
  if (props.includeSpecialTag) {
    return AVAILABLE_TAGS.value.find((tag: Tag): boolean => {
      return tag.name.includes(
        props.includeSpecialTag as AvailableSpecialTagNames,
      )
    })
  } else return undefined
})

function tagIdIsSelected(tagId: number): boolean {
  return props.modelValue.includes(tagId)
}

function handleTagElementClick(tagId: number): void {
  if (SPECIAL_TAG.value?.id === tagId) {
    if (!props.modelValue.includes(SPECIAL_TAG.value?.id))
      emit(
        'update:modelValue',
        SPECIAL_TAG.value?.id ? [SPECIAL_TAG.value?.id] : [],
      )
    else emit('update:modelValue', [])
  } else {
    const copyOfSelectedTagIds: number[] = [...props.modelValue]
    const index: number = copyOfSelectedTagIds.indexOf(tagId)
    const specialIndex: number = SPECIAL_TAG.value?.id
      ? copyOfSelectedTagIds.indexOf(SPECIAL_TAG.value?.id)
      : -1
    if (specialIndex !== -1) copyOfSelectedTagIds.splice(specialIndex, 1)
    if (
      index === -1 &&
      (!props.maxAmount || props.modelValue.length < props.maxAmount)
    )
      copyOfSelectedTagIds.push(tagId)
    else if (index !== -1) copyOfSelectedTagIds.splice(index, 1)
    emit('update:modelValue', copyOfSelectedTagIds)
  }
}
</script>

<template>
  <TagGridTemplate
    v-if="AVAILABLE_TAGS.length"
    class="selectFromType"
    :class="{ ignoreXContentPad }"
  >
    <template #title>
      <slot />
    </template>
    <template #default>
      <TagElement
        v-for="tag in AVAILABLE_TAGS"
        :key="tag.id"
        :tag="tag"
        :readonly="readonly"
        :i18nkey="typeName"
        :selected="tagIdIsSelected(tag.id)"
        :data-test-id="'tag__' + tag.name"
        @click="handleTagElementClick(tag.id)"
      />
    </template>
  </TagGridTemplate>
</template>
